"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useTranslation } from "@/hooks/useTranslation";
import { useUpdateCleanerPurchase } from "@/hooks/useCleanerPurchases";
import type { TurnoverPurchase } from "@/hooks/useCleanerPurchases";
import { toast } from "@/hooks/use-toast";

export interface EditTurnoverPurchaseSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purchase: TurnoverPurchase | null;
  propertyId: string;
  cleaningTaskId: string | null;
  onSaved?: () => void;
}

export function EditTurnoverPurchaseSheet({
  open,
  onOpenChange,
  purchase,
  propertyId,
  cleaningTaskId,
  onSaved,
}: EditTurnoverPurchaseSheetProps) {
  const { tDynamic } = useTranslation();
  const updatePurchase = useUpdateCleanerPurchase();
  const [amount, setAmount] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (purchase && open) {
      setAmount(purchase.amount > 0 ? String(purchase.amount) : "");
      setQuantity(purchase.quantity >= 0 ? purchase.quantity : 1);
      setNote(purchase.note ?? "");
    }
  }, [purchase, open]);

  const handleSave = async () => {
    if (!purchase) return;
    const numAmount = amount === "" ? undefined : parseFloat(amount);
    if (numAmount !== undefined && (isNaN(numAmount) || numAmount < 0)) {
      toast({ title: tDynamic("Invalid amount"), variant: "destructive" });
      return;
    }
    try {
      await updatePurchase.mutateAsync({
        purchaseId: purchase.id,
        propertyId,
        cleaningTaskId: cleaningTaskId ?? undefined,
        amount: numAmount,
        quantity: quantity >= 0 ? quantity : undefined,
        note: note.trim() || null,
      });
      toast({ title: tDynamic("Updated") });
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast({ title: tDynamic("Error"), variant: "destructive" });
    }
  };

  const displayName =
    purchase?.item_name ||
    (purchase?.line_items && purchase.line_items.length > 0
      ? purchase.line_items.map((li) => `${li.item_name ?? "Item"} (${li.quantity})`).join(", ")
      : tDynamic("Purchase"));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-auto max-h-[90vh] rounded-t-xl overflow-y-auto">
        <SheetHeader className="text-left pb-4">
          <SheetTitle>{tDynamic("Edit purchase")}: {displayName}</SheetTitle>
        </SheetHeader>
        {purchase && (
          <div className="space-y-4 pb-6">
            {purchase.type === "cleaner_purchase" && (
              <div>
                <Label>{tDynamic("Amount ($)")}</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="mt-1.5 h-12"
                />
              </div>
            )}
            {purchase.item_id != null && (
              <div>
                <Label>{tDynamic("Quantity")}</Label>
                <Input
                  type="number"
                  min={1}
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  className="mt-1.5 h-12"
                />
              </div>
            )}
            <div>
              <Label>{tDynamic("Note (optional)")}</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={tDynamic("Any additional details...")}
                className="mt-1.5"
                rows={2}
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
                {tDynamic("Cancel")}
              </Button>
              <Button onClick={handleSave} disabled={updatePurchase.isPending} className="flex-1">
                {updatePurchase.isPending ? tDynamic("Saving...") : tDynamic("Save")}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
